import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { 
  TrendingUp, TrendingDown, Users, Target, AlertTriangle, Award, 
  RefreshCw, BarChart3, CheckCircle, XCircle, Bell, BellOff
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, Area, AreaChart
} from 'recharts';
import { toast } from 'sonner';

const CHART_COLORS = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#0EA5E9'];

export default function MatchingAnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculando, setRecalculando] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voluntariado/analytics`);
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Erro ao carregar analytics');
    } finally {
      setLoading(false);
    }
  };

  const recalcularTodos = async () => {
    setRecalculando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voluntariado/recalcular-todos-matchings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      loadAnalytics();
    } catch (error) {
      toast.error('Erro ao recalcular matchings');
    } finally {
      setRecalculando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="analytics-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-slate-500">
        Não foi possível carregar os dados de analytics
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="matching-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-[#0066CC]" />
            Analytics de Matching
          </h1>
          <p className="text-slate-500 mt-1">Métricas de conversão e qualidade dos matchings de voluntariado</p>
        </div>
        <Button 
          onClick={recalcularTodos}
          variant="outline"
          disabled={recalculando}
          data-testid="recalcular-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${recalculando ? 'animate-spin' : ''}`} />
          {recalculando ? 'A calcular...' : 'Recalcular Todos'}
        </Button>
      </div>

      {/* Funil de Conversão */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#0066CC]" />
            Funil de Conversão
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1: Notificações */}
            <div className="text-center relative">
              <div className="w-24 h-24 rounded-full bg-blue-500 flex items-center justify-center mx-auto mb-4">
                <Bell className="w-10 h-10 text-white" />
              </div>
              <p className="text-4xl font-bold text-blue-600">{analytics.conversao.total_notificados}</p>
              <p className="text-slate-500">Notificações Enviadas</p>
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2 hidden md:block">
                <div className="text-2xl text-slate-300">→</div>
              </div>
            </div>
            
            {/* Step 2: Candidaturas */}
            <div className="text-center relative">
              <div className="w-24 h-24 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-white" />
              </div>
              <p className="text-4xl font-bold text-amber-600">{analytics.conversao.total_candidaturas}</p>
              <p className="text-slate-500">Candidaturas</p>
              <Badge className="mt-2 bg-amber-100 text-amber-700">
                {analytics.conversao.taxa_notificacao_candidatura}% conversão
              </Badge>
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2 hidden md:block">
                <div className="text-2xl text-slate-300">→</div>
              </div>
            </div>
            
            {/* Step 3: Confirmações */}
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <p className="text-4xl font-bold text-green-600">{analytics.conversao.total_confirmados}</p>
              <p className="text-slate-500">Confirmações</p>
              <Badge className="mt-2 bg-green-100 text-green-700">
                {analytics.conversao.taxa_candidatura_confirmacao}% conversão
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Score Comparação e Distribuição */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Score Médio: Sucesso vs Falha */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-500" />
              Score Médio: Sucesso vs Falha
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Matches Sucesso', score: analytics.scores.score_medio_sucesso, fill: '#22C55E' },
                  { name: 'Matches Falha', score: analytics.scores.score_medio_falha, fill: '#EF4444' }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                    {[0, 1].map((index) => (
                      <Cell key={index} fill={index === 0 ? '#22C55E' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 text-center">
              <Badge variant="outline" className={analytics.scores.diferenca > 0 ? 'border-green-500 text-green-600' : 'border-red-500 text-red-600'}>
                Diferença: {analytics.scores.diferenca > 0 ? '+' : ''}{analytics.scores.diferenca} pontos
              </Badge>
              <p className="text-xs text-slate-500 mt-2">
                Matches confirmados têm em média score {analytics.scores.diferenca > 0 ? 'maior' : 'menor'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Distribuição de Scores */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              Distribuição de Scores ({analytics.total_matchings} matchings)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.distribuicao_scores}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {analytics.distribuicao_scores.map((entry, index) => (
                      <Cell key={index} fill={CHART_COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-4">
              {analytics.distribuicao_scores.map((entry, index) => (
                <div key={entry.range} className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: CHART_COLORS[index] }}></div>
                  <span className="text-xs text-slate-500">{entry.range}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Voluntários e Missões Difíceis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 10 Voluntários */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Top 10 Voluntários Mais Compatíveis
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.top_voluntarios.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Nenhum voluntário com matchings suficientes
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>#</TableHead>
                    <TableHead>Voluntário</TableHead>
                    <TableHead className="text-center">Score Médio</TableHead>
                    <TableHead className="text-center">Matchings</TableHead>
                    <TableHead className="text-center">Missões</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analytics.top_voluntarios.map((vol, index) => (
                    <TableRow key={vol.voluntario_id} className="hover:bg-slate-50">
                      <TableCell>
                        <Badge className={index < 3 ? 'bg-amber-500' : 'bg-slate-400'}>
                          {index + 1}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{vol.voluntario_nome}</TableCell>
                      <TableCell className="text-center">
                        <span className={`font-bold ${vol.score_medio >= 75 ? 'text-green-600' : vol.score_medio >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {vol.score_medio}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">{vol.total_matchings}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{vol.missoes_concluidas}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Missões Difíceis de Preencher */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Missões Difíceis de Preencher
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.missoes_dificeis.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                Todas as missões têm bons matchings!
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Missão</TableHead>
                    <TableHead className="text-center">Score Médio</TableHead>
                    <TableHead className="text-center">Matchings</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analytics.missoes_dificeis.map((missao) => (
                    <TableRow key={missao.missao_id} className="hover:bg-slate-50">
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {missao.titulo}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-red-500">{missao.score_medio}</Badge>
                      </TableCell>
                      <TableCell className="text-center">{missao.num_matches}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-red-600">
                          Precisa atenção
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Voluntários Inativos */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <BellOff className="w-5 h-5 text-slate-500" />
            Voluntários com Muitos Matches mas Nunca Candidataram
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analytics.voluntarios_inativos.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
              Todos os voluntários estão engajados!
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Voluntário</TableHead>
                  <TableHead className="text-center">Total Matches</TableHead>
                  <TableHead className="text-center">Candidaturas</TableHead>
                  <TableHead>Sugestão</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics.voluntarios_inativos.map((vol) => (
                  <TableRow key={vol.voluntario_id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{vol.voluntario_nome}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{vol.total_matches}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-red-100 text-red-700">{vol.candidaturas}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-amber-600 text-sm">{vol.sugestao}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="text-slate-600">
                        <BellOff className="w-4 h-4 mr-1" />
                        Desativar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
